import React, { useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import { Add } from "@mui/icons-material";
import { EditVideoDialog } from "../video/EditVideoDialog";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import { useSnackbar } from "notistack";
import { CopyVideoDialog } from "./CopyVideoDialog";

export const VideoItem = ({ id, displayname, onVideoUpdate, allVideos }) => {
  const { token } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [openEditDialog, setOpenEditDialog] = useState(false); // New state for confirmation dialog
  const [openCopyDialog, setOpenCopyDialog] = useState(false); // New state for confirmation dialog
  const [videoToEdit, setVideoToEdit] = useState(null); // New state for the user to be deleted
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/video/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setVideoToEdit(response.data);
    setOpenEditDialog(true);
  };

  const handleCopyClick = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/video/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setVideoToEdit(response.data);
    setOpenCopyDialog(true);
  };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
  };

  const handleCancelCopy = () => {
    setOpenCopyDialog(false);
  };

  const handleEditVideo = async (data) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/video/${data.id}`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onVideoUpdate(response.data); // Update the parent state
      enqueueSnackbar("Successfully Changed!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to update video!", { variant: "error" });
    }
  };

  const handleCopyVideo = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/video`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onVideoUpdate(response.data); // Update the parent state
      enqueueSnackbar("Successfully Copied!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to copy video!", { variant: "error" });
    }
  };

  const handleDeleteVideo = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/video/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      onVideoUpdate({ id, deleted: true }); // Notify parent about the deletion
      setOpenEditDialog(false);
      enqueueSnackbar("Successfully Deleted!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to delete video!", { variant: "error" });
    }
  };

  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          width: "475px",
          height: "120px",
          padding: 1,
          borderRadius: "5px",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: darkMode
            ? getColors.thumbnailBorderDark
            : getColors.thumbnailBorderLight,
          background: darkMode
            ? getColors.thumbnnailBgDark
            : getColors.thumbnnailBgLight,
          cursor: "pointer",
          "&:hover": {
            borderColor: darkMode
              ? getColors.thumbnailTextDark
              : getColors.thumbnailTextLight,
          },
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 500,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              ml: "20px",
            }}
          >
            {displayname}
          </Typography>
        </Box>
        <Box display="flex">
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            width="max-content"
            sx={{
              borderRadius: "50%",
              padding: "20px",
              color: "#009903",
              bgcolor: "#EAFFEF",
              alignItems: "center",
              "&:hover": {
                color: "green",
              },
              ml: 1,
              mr: 2,
            }}
            onClick={handleCopyClick} // Move the onClick handler here
          >
            <Add />
          </Box>
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            width="max-content"
            sx={{
              borderRadius: "50%",
              padding: "20px",
              color: "#007FFF",
              bgcolor: "#E9F3FF",
              alignItems: "center",
              "&:hover": {
                color: "blue",
              },
              mr: 2,
            }}
            onClick={handleClick} // Move the onClick handler here
          >
            <BorderColorOutlinedIcon />
          </Box>
        </Box>
      </Box>
      <EditVideoDialog
        open={openEditDialog}
        onClose={handleCancelEdit}
        videoData={videoToEdit}
        onSave={handleEditVideo}
        onRemove={handleDeleteVideo}
        allVideos={allVideos}
      />
      <CopyVideoDialog
        open={openCopyDialog}
        onClose={handleCancelCopy}
        videoData={videoToEdit}
        allVideos={allVideos}
        onSave={handleCopyVideo}
      />
    </>
  );
};
